import React, { useState, useRef, useCallback, useEffect } from "react";
import { API_BASE, apiFetch } from "../../api/http";
import ClientTemplateListModal from "./ClientTemplateListModal";

export interface UploadClientTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (data: {
    file: File;
    documentType: "protocol" | "protective" | "clause";
    clientName: string;
    projectNumber: string;
    autoApply: boolean;
  }) => void;
}

// Base URL moved to shared api/http; behavior unchanged

const UploadClientTemplateModal: React.FC<UploadClientTemplateModalProps> = ({ isOpen, onClose, onUpload }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<"protocol" | "protective" | "clause">("protocol");
  const [clientName, setClientName] = useState<string>("");
  const [autoApply, setAutoApply] = useState<boolean>(false);
  const [projectNumber, setProjectNumber] = useState<string>("");
  // Track which types have already been uploaded for this client
  const [usedTypes, setUsedTypes] = useState<Array<"protocol" | "protective" | "clause">>([]);
  const [isListOpen, setIsListOpen] = useState<boolean>(false);

  // Load saved clientName and projectNumber on first mount
  useEffect(() => {
    const savedName = localStorage.getItem('clientName');
    if (savedName) setClientName(savedName);
    const savedNumber = localStorage.getItem('projectNumber');
    if (savedNumber) setProjectNumber(savedNumber);
  }, []);

  // Function to load which types have been uploaded for this client
  const fetchUsedTypes = async () => {
    if (!clientName || !projectNumber) {
      setUsedTypes([]);
      return;
    }
    try {
      const res = await apiFetch(
        `${API_BASE}/api/clientTemplates?clientName=${encodeURIComponent(clientName)}&projectNumber=${encodeURIComponent(projectNumber)}`
      );
      if (!res.ok) {
        throw new Error('Failed to fetch client templates');
      }
      const data: Array<{ documentType: string }> = await res.json();
      setUsedTypes(
        data.map(item => item.documentType as "protocol" | "protective" | "clause")
      );
    } catch (err) {
      console.error('Error fetching client templates:', err);
    }
  };

  // Reload usedTypes whenever modal opens or clientName or projectNumber changes
  useEffect(() => {
    if (isOpen) {
      fetchUsedTypes();
    }
  }, [isOpen, clientName, projectNumber]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
  };

  // Drag&Drop convenience
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const resetAndClose = useCallback(() => {
    // Reset fields when closing
    setSelectedFile(null);
    setDocumentType("protocol");
    // keep clientName saved
    setAutoApply(false);
    onClose();
  }, [onClose]);

  const handleUpload = () => {
    if (!selectedFile) return;
    if (!/^[0-9]+$/.test(projectNumber)) {
      alert('Project Number must be numeric.');
      return;
    }
    onUpload({ file: selectedFile, documentType, clientName, projectNumber, autoApply });
    resetAndClose();
  };

  const openList = () => setIsListOpen(true);
  const closeList = () => setIsListOpen(false);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={resetAndClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close (X) */}
        <button
          aria-label="Close"
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-xl"
          onClick={resetAndClose}
        >
          &times;
        </button>

        <h2 className="text-xl font-semibold mb-4">Upload Client Template</h2>

        {/* Drag & Drop Area */}
        <div
          className={`border-2 border-dashed border-gray-300 rounded-lg p-6 text-center mb-4 ${
            usedTypes.includes(documentType) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
          onDragOver={usedTypes.includes(documentType) ? undefined : handleDragOver}
          onDrop={usedTypes.includes(documentType) ? undefined : handleDrop}
          onClick={usedTypes.includes(documentType) ? undefined : handleBrowseClick}
        >
          {usedTypes.includes(documentType) ? (
            <p className="text-red-500 font-medium">Template for this type already uploaded.</p>
          ) : selectedFile ? (
            <span className="font-medium text-gray-700">{selectedFile.name}</span>
          ) : (
            <>
              <p className="text-gray-500">Drag and drop a file here, or click to browse [Please don't click, this Feature will not work as under development]</p>
              <p className="mt-2 text-sm text-gray-400">Example_ESI_Protocol.docx</p>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".doc,.docx,.pdf,.csv,.json"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        {/* Document Type */}
        <div className="mb-4">
          <p className="font-medium mb-2">Document Type</p>
          <div className="flex space-x-4">
            <label className="flex items-center space-x-1 cursor-pointer">
              <input
                type="radio"
                name="doctype"
                value="protocol"
                checked={documentType === "protocol"}
                onChange={() => setDocumentType("protocol")}
                disabled={usedTypes.includes("protocol")}
              />
              <span>ESI Protocol</span>
            </label>
            <label className="flex items-center space-x-1 cursor-pointer">
              <input
                type="radio"
                name="doctype"
                value="protective"
                checked={documentType === "protective"}
                onChange={() => setDocumentType("protective")}
                disabled={usedTypes.includes("protective")}
              />
              <span>Protective Order</span>
            </label>
            <label className="flex items-center space-x-1 cursor-pointer">
              <input
                type="radio"
                name="doctype"
                value="clause"
                checked={documentType === "clause"}
                onChange={() => setDocumentType("clause")}
                disabled={usedTypes.includes("clause")}
              />
              <span>Clause Library</span>
            </label>
          </div>
        </div>

        {/* Client Name */}
        <div className="mb-4">
          <p className="font-medium mb-1">Client Name or identifier</p>
          <input
            type="text"
            value={clientName}
            onChange={(e) => { setClientName(e.target.value); localStorage.setItem('clientName', e.target.value); }}
            placeholder="Client A"
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400"
          />
        </div>
        {/* Project Number */}
        <div className="mb-4">
          <p className="font-medium mb-1">Client or Project Number</p>
          <input
            type="text"
            value={projectNumber}
            onChange={(e) => {
              // Only allow numbers
              const val = e.target.value;
              if (/^\d*$/.test(val)) {
                setProjectNumber(val);
                localStorage.setItem('projectNumber', val);
              }
            }}
            placeholder="12345"
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400"
            inputMode="numeric"
            pattern="[0-9]*"
          />
        </div>

        {/* Buttons */}
        <div className="flex justify-between items-center mt-6">
          <button
            onClick={openList}
            className="text-sm text-brand-500 hover:underline"
          >
            View uploads
          </button>
          <div className="space-x-2">
            <button
              onClick={resetAndClose}
              className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={!selectedFile || !clientName || !projectNumber || !/^[0-9]+$/.test(projectNumber) || usedTypes.includes(documentType)}
              className="px-4 py-2 rounded bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50"
            >
              Upload
            </button>
          </div>
        </div>

        {/* Modal for list */}
        <ClientTemplateListModal
          isOpen={isListOpen}
          onClose={closeList}
          clientName={clientName}
          projectNumber={projectNumber}
          onDeleted={fetchUsedTypes}
        />
      </div>
    </div>
  );
};

export default UploadClientTemplateModal; 